import { useEffect, useMemo } from "react";

import type { Action } from "@/actions/action-types";
import { IconOnaCopy } from "@/assets/icons/IconOnaCopy";
import { IconOnaDownload } from "@/assets/icons/IconOnaDownload";
import { IconOnaReset } from "@/assets/icons/IconOnaReset";
import { useAgentExecutionActions } from "@/hooks/use-agent-execution-actions";
import { useCompleteSupportBundle } from "@/hooks/use-complete-support-bundle";
import { getAgentExecution, useEnvironmentConversation } from "@/hooks/use-environment-conversation";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useAgentExecutionsForEnvironment } from "@/queries/agent-queries";
import { useEnvironment, type PlainEnvironment } from "@/queries/environment-queries";

// Stub used when environment data hasn't loaded yet. The hook safely handles
// undefined status (supportBundleUrl will be falsy, so environment.id is never accessed).
const EMPTY_ENVIRONMENT = { id: "", status: undefined } as unknown as PlainEnvironment;

type UseSessionActionsOptions = {
    /** Called when the user selects "Reset conversation". The consumer renders the confirmation modal. */
    onResetSession?: (agentExecution: PlainAgentExecution) => void;
};

/**
 * Returns command palette actions derived from the active agent execution
 * on an environment: reset conversation, copy session ID, download support bundle.
 */
export function useSessionCommandPaletteActions(
    environmentId: string,
    options: UseSessionActionsOptions = {},
): { sessionActions: Action[]; troubleshootingActions: Action[] } {
    const { onResetSession } = options;

    const { environmentConversation } = useEnvironmentConversation(environmentId);
    const agentExecution = getAgentExecution(environmentConversation);
    const { copyAgentExecutionId } = useAgentExecutionActions(agentExecution ?? undefined);
    const { data: environment } = useEnvironment(environmentId);
    const { data: allExecutions } = useAgentExecutionsForEnvironment(environmentId);
    const { downloadSupportBundle, isSupportBundleAvailable, enableTokenFetch } = useCompleteSupportBundle(
        environment ?? EMPTY_ENVIRONMENT,
        allExecutions,
    );

    // Eagerly enable token fetch — command palette doesn't use deferred loading.
    useEffect(() => {
        enableTokenFetch();
    }, [enableTokenFetch]);

    const sessionActions = useMemo((): Action[] => {
        if (!onResetSession) return [];
        return [
            {
                id: "reset-session",
                label: "Reset conversation",
                icon: <IconOnaReset size="sm" />,
                keywords: ["reset", "session", "clear", "delete"],
                onSelect: () => {
                    if (agentExecution) onResetSession(agentExecution);
                },
                disabled: !agentExecution,
                variant: "destructive",
                group: "lifecycle",
                priority: 2,
            },
        ];
    }, [agentExecution, onResetSession]);

    const troubleshootingActions = useMemo(
        (): Action[] => [
            {
                id: "copy-session-id",
                label: "Copy conversation ID",
                icon: <IconOnaCopy size="sm" />,
                keywords: ["troubleshoot", "debug", "copy", "session", "id"],
                onSelect: () => void copyAgentExecutionId(),
                disabled: !agentExecution,
            },
            {
                id: "download-support-bundle",
                label: "Download support bundle",
                icon: <IconOnaDownload size="sm" />,
                keywords: ["troubleshoot", "debug", "support", "bundle", "download"],
                onSelect: () => void downloadSupportBundle(),
                disabled: !isSupportBundleAvailable,
            },
        ],
        [agentExecution, copyAgentExecutionId, downloadSupportBundle, isSupportBundleAvailable],
    );

    return { sessionActions, troubleshootingActions };
}
